import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Users, Disc3, Star, Loader2, ChevronDown, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import api from "../utils/api";
import { usePublicAuth } from "../context/PublicAuthContext";
import { LabelManagerBadge } from "../components/LabelManagerBadge";

const FILTERS = [
  { id: "all", label: "Alle" },
  { id: "artist", label: "Artists" },
  { id: "supporter", label: "Supporter" },
  { id: "label", label: "Labels" },
];

function MemberCard({ member }) {
  const isArtist = member.is_artist;
  const isSupporter = member.is_supporter;
  const isLabelManager = member.is_label_manager;
  const { t } = useTranslation();

  return (
    <Link
      to={`/user/${encodeURIComponent(member.username)}`}
      data-testid={`member-card-${member.username}`}
      className="group flex flex-col items-center gap-3.5 p-5 rounded-2xl bg-zinc-900/50 border border-white/[0.05] hover:border-white/10 hover:bg-zinc-900/70 transition-all duration-200"
    >
      {/* Avatar */}
      <div className="relative flex-shrink-0">
        <div className={`w-20 h-20 rounded-2xl overflow-hidden ring-2 ${isArtist ? "ring-blue-500/70" : isLabelManager ? "ring-emerald-500/50" : isSupporter ? "ring-rose-500/60" : "ring-zinc-700"}`}>
          {(member.avatar_url && !member.avatar_url.includes("gravatar.com"))
            ? <img src={member.avatar_url} alt={member.username} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
            : (
              <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                <span className="text-2xl font-black text-zinc-500">{(member.username || "?")[0].toUpperCase()}</span>
              </div>
            )
          }
        </div>
        {isArtist && (
          <span className="absolute -bottom-1.5 -right-1.5 w-6 h-6 bg-blue-600 rounded-full border-2 border-zinc-900 flex items-center justify-center shadow-[0_0_8px_rgba(59,130,246,0.4)]">
            <Disc3 size={11} className="text-white" />
          </span>
        )}
        {isLabelManager && !isArtist && (
          <span className="absolute -bottom-1.5 -right-1.5 w-6 h-6 rounded-full border-2 border-zinc-900 flex items-center justify-center"
            style={{ background: "#10b981", boxShadow: "0 0 8px rgba(16,185,129,0.4)" }}>
            <Disc3 size={11} className="text-white" />
          </span>
        )}
        {isSupporter && (
          <span className={`absolute -bottom-1.5 ${isArtist || isLabelManager ? '-left-1.5' : '-right-1.5'} w-6 h-6 bg-rose-600 rounded-full border-2 border-zinc-900 flex items-center justify-center shadow-[0_0_8px_rgba(225,29,72,0.4)]`}>
            <Star size={10} className="text-white fill-white" />
          </span>
        )}
      </div>

      {/* Name */}
      <div className="text-center w-full">
        <p className="text-white text-sm font-bold truncate">{member.username}</p>
        {member.artist_name && (
          <p className="text-blue-400/80 text-xs mt-0.5 truncate">{member.artist_name}</p>
        )}
        {isLabelManager && member.label_name && !isArtist && (
          <p className="text-xs mt-0.5 truncate" style={{ color: "#10b981" }}>{member.label_name}</p>
        )}
      </div>

      {/* Badges */}
      <div className="flex items-center gap-1.5 flex-wrap justify-center">
        {member.role === "developer" && (
          <span className="text-[10px] font-black tracking-widest uppercase text-orange-400">{t("community.admin")}</span>
        )}
        {member.role === "developer" && (
          <span className="text-[10px] font-black tracking-widest uppercase text-emerald-400">{t("community.founder")}</span>
        )}
        {member.role === "admin" && (
          <span className="text-[10px] font-black tracking-widest uppercase text-amber-400">{t("community.team")}</span>
        )}
        {isArtist && (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-blue-600 text-white">
            <Disc3 size={9} /> {t("common.artist")}
          </span>
        )}
        {isLabelManager && (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full text-white"
            style={{ background: "#10b981" }}>
            <Disc3 size={9} /> Label
          </span>
        )}
        {isSupporter && (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-rose-600 text-white">
            <Star size={9} className="fill-white" /> {t("common.supporter")}
          </span>
        )}
        {!isArtist && !isSupporter && !isLabelManager && member.role !== "admin" && member.role !== "developer" && (
          <span className="text-[10px] text-zinc-700 font-medium">{t("common.member")}</span>
        )}
      </div>
    </Link>
  );
}

export default function CommunityPage() {
  const { publicUser, isPublicAuth, isPublicLoading, login } = usePublicAuth();
  const { t } = useTranslation();
  const [members, setMembers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const LIMIT = 48;

  const FILTERS = [
    { id: "all", label: t("community.filter_all") },
    { id: "artist", label: t("community.filter_artists") },
    { id: "supporter", label: t("community.filter_supporters") },
    { id: "label", label: "Labels" },
  ];

  const load = useCallback(async (f, o, reset = false) => {
    setLoading(true);
    try {
      const res = await api.get(`/community/members?filter=${f}&limit=${LIMIT}&offset=${o}`);
      const data = res.data;
      setTotal(data.total);
      if (reset) {
        setMembers(data.members);
      } else {
        setMembers(prev => [...prev, ...data.members]);
      }
      setHasMore(o + LIMIT < data.total);
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    setOffset(0);
    load(filter, 0, true);
  }, [filter, load]);

  const handleLoadMore = () => {
    const newOffset = offset + LIMIT;
    setOffset(newOffset);
    load(filter, newOffset, false);
  };

  return (
    <div className="min-h-screen bg-[#080808] text-white">
      <Helmet>
        <title>Community – Mitglieder | HUWND</title>
        <meta name="description" content="Die HUWND Hands-Up Community – entdecke andere Fans, Artists und Supporter." />
        <link rel="canonical" href="https://handsupwillneverdie.de/community" />
        <meta property="og:site_name" content="HUWND – Hands Up Will Never Die" />
        <meta property="og:title" content="Community | HUWND" />
        <meta property="og:url" content="https://handsupwillneverdie.de/community" />
        <meta name="robots" content="noindex" />
      </Helmet>
      <Header />

      {/* Login Gate für nicht-eingeloggte User */}
      {!isPublicLoading && !isPublicAuth && (
        <div className="max-w-md mx-auto px-4 py-24 text-center">
          <div className="w-16 h-16 bg-zinc-900 border border-white/5 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <Lock size={24} className="text-zinc-600" />
          </div>
          <h1 className="text-2xl font-black text-white mb-3">{t("community.title")}</h1>
          <p className="text-zinc-500 text-sm leading-relaxed mb-6">
            {t("community.login_required_desc")}
          </p>
          <button
            onClick={() => login()}
            className="inline-flex items-center gap-2 px-6 py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold text-sm transition-colors"
          >
            {t("community.login_btn")}
          </button>
        </div>
      )}

      {isPublicAuth && (
      <div className="max-w-6xl mx-auto px-4 py-12 sm:py-16">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Users size={22} className="text-rose-400" />
            <h1 className="text-3xl font-black text-white">{t("community.title")}</h1>
          </div>
          <p className="text-zinc-500 text-sm">
            {t("community.subtitle")}
            {total > 0 && <span className="text-zinc-600 ml-1">· {t("community.member_count", { count: total })}</span>}
          </p>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mb-8">
          {FILTERS.map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`filter-${f.id}`}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${
                filter === f.id
                  ? "bg-rose-600 text-white shadow-[0_0_12px_rgba(225,29,72,0.3)]"
                  : "bg-zinc-900/60 border border-white/5 text-zinc-500 hover:text-white hover:border-white/10"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Loading initial */}
        {loading && members.length === 0 && (
          <div className="flex justify-center py-20">
            <Loader2 size={28} className="animate-spin text-zinc-600" />
          </div>
        )}

        {/* Empty */}
        {!loading && members.length === 0 && (
          <div className="text-center py-20">
            <Users size={40} className="text-zinc-800 mx-auto mb-4" />
            <p className="text-zinc-500 text-sm">{t("community.no_members")}</p>
          </div>
        )}

        {/* Grid */}
        {members.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {members.map(m => (
              <MemberCard key={m.username} member={m} />
            ))}
          </div>
        )}

        {/* Load more */}
        {hasMore && (
          <div className="flex justify-center mt-8">
            <button
              onClick={handleLoadMore}
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-zinc-900/60 border border-white/5 text-zinc-400 hover:text-white hover:border-white/10 text-sm font-medium transition-all disabled:opacity-50"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <ChevronDown size={14} />}
              {t("community.load_more")}
            </button>
          </div>
        )}
      </div>
      )} {/* end isPublicAuth */}

      <Footer />
    </div>
  );
}
